import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Minus, Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useCartStore } from "@/stores/cartStore";
import { formatPrice } from "@/lib/shopify";
import { shopifyImageUrl } from "@/lib/imageUtils";
import { formatProductTitle } from "@/lib/formatProductTitle";

// Global open trigger — components call openCartDrawer() after adding to cart
let _openDrawer: (() => void) | null = null;
export function openCartDrawer() {
  _openDrawer?.();
}

const CartBadge = () => {
  const totalItems = useCartStore((s) =>
    s.items.reduce((sum, i) => sum + i.quantity, 0)
  );
  if (totalItems === 0) return null;
  return (
    <span className="absolute -top-1.5 -right-1.5 bg-foreground text-primary-foreground text-[9px] font-body w-4 h-4 flex items-center justify-center rounded-full">
      {totalItems > 9 ? "9+" : totalItems}
    </span>
  );
};

const CartDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { items, isLoading, isSyncing, updateQuantity, removeItem, getCheckoutUrl, syncCart } =
    useCartStore();

  // Register global opener
  useEffect(() => {
    _openDrawer = () => setIsOpen(true);
    return () => { _openDrawer = null; };
  }, []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce(
    (sum, i) => sum + parseFloat(i.price.amount) * i.quantity,
    0
  );
  const currency = items[0]?.price.currencyCode || "EUR";

  useEffect(() => {
    if (isOpen) syncCart();
  }, [isOpen, syncCart]);

  const handleCheckout = () => {
    const url = getCheckoutUrl();
    if (url) {
      window.open(url, "_blank");
      setIsOpen(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button
          aria-label="Cart"
          className="relative text-muted-foreground hover:text-foreground transition-colors"
        >
          <ShoppingBag size={18} />
          <CartBadge />
        </button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md flex flex-col h-full p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border flex-shrink-0">
          <SheetTitle className="font-display text-lg font-semibold">
            Your Cart ({totalItems})
          </SheetTitle>
          <SheetDescription className="font-body text-xs text-muted-foreground">
            {totalItems === 0
              ? "Your cart is empty"
              : "Free shipping on orders over €180 within EU"}
          </SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6">
            <ShoppingBag size={40} className="text-muted-foreground/30 mb-4" />
            <p className="font-body text-sm text-muted-foreground mb-6">
              Your cart is empty
            </p>
            <Button variant="outline" size="sm" onClick={() => setIsOpen(false)} asChild>
              <Link to="/shop">Continue Shopping</Link>
            </Button>
          </div>
        ) : (
          <>
            {/* Line items */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {items.map((item) => {
                const img = item.product.node.images?.edges?.[0]?.node;
                const title = formatProductTitle(item.product.node.title);
                return (
                  <div key={item.variantId} className="flex gap-4">
                    <Link
                      to={`/product/${item.product.node.handle}`}
                      onClick={() => setIsOpen(false)}
                      className="w-20 h-24 bg-muted rounded-sm overflow-hidden shrink-0"
                    >
                      {img && (
                        <img
                          src={shopifyImageUrl(img.url, 160)}
                          alt={title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/product/${item.product.node.handle}`}
                        onClick={() => setIsOpen(false)}
                        className="font-body text-sm text-foreground hover:text-muted-foreground transition-colors line-clamp-1"
                      >
                        {title}
                      </Link>
                      {item.variantTitle !== "Default Title" && (
                        <p className="font-body text-xs text-muted-foreground mt-0.5">
                          {item.selectedOptions.map((o) => o.value).join(" / ")}
                        </p>
                      )}
                      <p className="font-body text-sm text-foreground mt-1">
                        {formatPrice(item.price.amount, item.price.currencyCode)}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex items-center border border-border">
                          <button
                            onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                            className="p-1 hover:bg-secondary transition-colors"
                            disabled={isLoading}
                          >
                            <Minus size={12} />
                          </button>
                          <span className="font-body text-xs w-7 text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                            className="p-1 hover:bg-secondary transition-colors"
                            disabled={isLoading}
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                        <button
                          onClick={() => removeItem(item.variantId)}
                          className="text-muted-foreground hover:text-foreground transition-colors ml-auto"
                          disabled={isLoading}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t border-border px-6 py-5 space-y-4 bg-background">
              <div className="flex justify-between items-center">
                <span className="font-body text-sm text-muted-foreground">Subtotal</span>
                <span className="font-display text-lg font-semibold text-foreground">
                  {formatPrice(totalPrice.toFixed(2), currency)}
                </span>
              </div>
              <p className="font-body text-[10px] text-muted-foreground">
                Shipping & taxes calculated at checkout
              </p>
              <Button
                variant="hero"
                size="lg"
                className="w-full"
                onClick={handleCheckout}
                disabled={isLoading || isSyncing}
              >
                {isLoading || isSyncing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Checkout
              </Button>
              <button
                onClick={() => setIsOpen(false)}
                className="w-full text-center font-body text-xs text-muted-foreground hover:text-foreground transition-colors underline"
              >
                Continue Shopping
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
